import { useState } from "react";
import { Check, MessageCircle, Pencil, Send, Trash2 } from "lucide-react";
import { MessagePreviewModal } from "./MessagePreviewModal";

const welcomeLabel = (r) => {
  if (r.welcome_wa_status === "failed") return "welcome failed";
  if (["delivered", "read"].includes(r.welcome_wa_status)) return `welcome ${r.welcome_wa_status}`;
  return r.welcome_wa_sent_at ? "welcome sent · awaiting delivery" : "welcome pending";
};
const welcomeDelivered = (r) => ["delivered", "read"].includes(r.welcome_wa_status);
const welcomeTone = (r) => (r.welcome_wa_status === "failed" ? "bg-red-50 text-red-700" : welcomeDelivered(r) ? "bg-emerald-50 text-emerald-700" : r.welcome_wa_sent_at ? "bg-sky-50 text-sky-700" : "bg-amber-50 text-amber-700");
const welcomeDot = (r) => (r.welcome_wa_status === "failed" ? "bg-red-600" : welcomeDelivered(r) ? "bg-emerald-600" : r.welcome_wa_sent_at ? "bg-sky-500" : "bg-amber-500");
const welcomeTitle = (r) => r.welcome_wa_error || (welcomeDelivered(r) ? `WhatsApp confirmed the welcome was ${r.welcome_wa_status}` : r.welcome_wa_sent_at ? "Meta accepted the message — delivery not confirmed yet (needs delivery receipts connected in the WhatsApp API panel)" : "Welcome not yet sent on WhatsApp");

export const RegistrationsTable = ({
  rows,
  visibleRows,
  agreedFilter,
  setAgreedFilter,
  selected,
  toggleAll,
  toggleSelect,
  startEdit,
  deleteOne,
  content,
  adminKey,
  authFetch,
  onSent,
}) => {
  const [target, setTarget] = useState(null);
  const openPreview = (r, channel) => setTarget({ r, channel });
  return (
  <div className="bg-white/80 border border-white/60 rounded-3xl shadow-[0_8px_40px_rgba(26,46,38,0.08)] overflow-hidden">
    <MessagePreviewModal target={target} adminKey={adminKey} authFetch={authFetch} content={content} onClose={() => setTarget(null)} onSent={onSent} />
    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-border/60">
      <p className="text-sm text-muted-foreground">Showing <span className="font-medium text-foreground">{visibleRows.length}</span> of {rows.length} registration(s)</p>
      <div className="inline-flex items-center gap-1 rounded-full border border-border bg-background/60 p-1" data-testid="agreed-filter">
        {[["all", "All", rows.length], ["yes", "Agreed", rows.filter((r) => r.checklist_agreed).length], ["no", "Not agreed", rows.filter((r) => !r.checklist_agreed).length]].map(([val, label, cnt]) => (
          <button
            key={val} data-testid={`agreed-filter-${val}`} onClick={() => setAgreedFilter(val)}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs transition-colors ${agreedFilter === val ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
          >
            {label}
            <span data-testid={`agreed-filter-count-${val}`} className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full text-[11px] font-medium ${agreedFilter === val ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted text-foreground/70"}`}>{cnt}</span>
          </button>
        ))}
      </div>
    </div>
    <div className="overflow-auto max-h-[70vh]">
      <table className="w-full text-sm" data-testid="registrations-table">
        <thead>
          <tr className="bg-primary/5 text-left">
            <th className="px-4 py-4 w-10">
              <input type="checkbox" data-testid="select-all"
                checked={visibleRows.length > 0 && visibleRows.every((r) => selected.includes(r.id))}
                onChange={toggleAll} className="w-4 h-4 accent-[#1A2E26] cursor-pointer" />
            </th>
            <th className="px-4 py-4 font-medium whitespace-nowrap">S.No</th>
            {["Name", "Mobile", "WhatsApp", "Email", "Profession", "Place", "Program Name", "Batch#", "Registered"].map((h) => (
              <th key={h} className="px-5 py-4 font-medium whitespace-nowrap">{h}</th>
            ))}
            <th className="px-5 py-4 font-medium whitespace-nowrap text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.length === 0 && (
            <tr><td colSpan={12} className="px-5 py-10 text-center text-muted-foreground">{rows.length === 0 ? "No registrations yet." : "No registrations match this filter."}</td></tr>
          )}
          {visibleRows.map((r, i) => (
            <tr key={r.id || i} className={`border-t border-border/60 transition-colors ${selected.includes(r.id) ? "bg-accent/10" : "hover:bg-primary/5"}`}>
              <td className="px-4 py-4">
                <input type="checkbox" data-testid={`select-reg-${i}`}
                  checked={selected.includes(r.id)} onChange={() => toggleSelect(r.id)}
                  className="w-4 h-4 accent-[#1A2E26] cursor-pointer" />
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-muted-foreground">{rows.indexOf(r) + 1}</td>
              <td className="px-5 py-4 whitespace-nowrap">
                <div>{r.name}</div>
                {r.checklist_agreed && (
                  <span data-testid={`reg-agreed-${r.id}`} title={r.checklist_agreed_at ? `Agreed on ${new Date(r.checklist_agreed_at).toLocaleString()}` : "Agreed to checklist"} className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[11px] font-medium">
                    <Check size={11} /> Agreed{r.checklist_agreed_at ? ` · ${new Date(r.checklist_agreed_at).toLocaleDateString()}` : ""}
                  </span>
                )}
              </td>
              <td className="px-5 py-4 whitespace-nowrap">{r.mobile}</td>
              <td className="px-5 py-4 whitespace-nowrap">
                {r.whatsapp && r.whatsapp !== r.mobile ? r.whatsapp : <span className="text-muted-foreground">{r.whatsapp ? "same" : "—"}</span>}
                <span data-testid={`reg-welcome-status-${i}`} title={welcomeTitle(r)} className={`ml-2 inline-flex items-center gap-1 text-[10px] rounded-full px-1.5 py-0.5 ${welcomeTone(r)}`} data-welcome={r.welcome_wa_status === "failed" ? "failed" : welcomeDelivered(r) ? "delivered" : r.welcome_wa_sent_at ? "sent" : "pending"}><span className={`grid place-items-center w-3.5 h-3.5 rounded-full text-white text-[9px] font-bold ${welcomeDot(r)}`}>{r.welcome_wa_status === "failed" ? "✕" : welcomeDelivered(r) ? "✓" : r.welcome_wa_sent_at ? "→" : "…"}</span>{welcomeLabel(r)}</span>
                {r.last_wa_template && r.last_wa_template !== content?.wa_welcome_template_name && (
                  <span data-testid={`reg-last-wa-status-${i}`} title={r.last_wa_error || (r.last_wa_sent_at ? new Date(r.last_wa_sent_at).toLocaleString() : "")} className={`ml-1 inline-block text-[10px] rounded-full px-1.5 py-0.5 ${r.last_wa_status === "failed" ? "bg-red-50 text-red-700" : ["delivered", "read"].includes(r.last_wa_status) ? "bg-emerald-50 text-emerald-700" : "bg-sky-50 text-sky-700"}`}>{r.last_wa_template} {r.last_wa_status === "failed" ? "failed" : r.last_wa_status === "sent" ? "✓" : r.last_wa_status}</span>
                )}
                {(r.welcome_wa_status === "failed" || !r.welcome_wa_sent_at) && content?.wa_api_enabled !== false && (
                  <button data-testid={`reg-welcome-resend-${i}`} onClick={() => openPreview(r, "whatsapp")} title="Send a template via WhatsApp API" className="ml-1 text-[10px] underline text-[#128C7E]">send</button>
                )}
              </td>
              <td className="px-5 py-4 whitespace-nowrap">
                {r.email}
                {r.last_email_template && <span data-testid={`reg-email-status-${i}`} title={`${r.last_email_subject || ""} · ${r.last_email_sent_at ? new Date(r.last_email_sent_at).toLocaleString() : ""}`} className="ml-2 inline-block text-[10px] rounded-full px-1.5 py-0.5 bg-sky-50 text-sky-700">{r.last_email_template} ✓</span>}
              </td>
              <td className="px-5 py-4 whitespace-nowrap">{r.profession}</td>
              <td className="px-5 py-4 whitespace-nowrap">{r.place}</td>
              <td className="px-5 py-4 whitespace-nowrap">{r.program}</td>
              <td className="px-5 py-4 whitespace-nowrap">{r.batch}</td>
              <td className="px-5 py-4 whitespace-nowrap text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</td>
              <td className="px-5 py-4 whitespace-nowrap text-right">
                <div className="inline-flex gap-2">
                  <button data-testid={`wa-reg-${i}`} onClick={() => openPreview(r, "whatsapp")} title="Send a WhatsApp template to this participant — choose the template, preview, confirm" className="grid place-items-center w-8 h-8 rounded-full border border-[#25D366] text-[#128C4B] hover:bg-[#25D366] hover:text-white transition-colors">
                    <MessageCircle size={14} />
                  </button>
                  <button data-testid={`send-zoom-reg-${i}`} onClick={() => openPreview(r, "email")} title="Email a template to this participant — choose the template, preview, confirm" className="grid place-items-center w-8 h-8 rounded-full border border-accent/50 text-accent hover:bg-accent hover:text-white transition-colors">
                    <Send size={14} />
                  </button>
                  <button data-testid={`edit-reg-${i}`} onClick={() => startEdit(r)} title="Edit" className="grid place-items-center w-8 h-8 rounded-full border border-border hover:bg-primary hover:text-primary-foreground transition-colors">
                    <Pencil size={14} />
                  </button>
                  <button data-testid={`delete-reg-${i}`} onClick={() => deleteOne(r)} title="Delete" className="grid place-items-center w-8 h-8 rounded-full border border-destructive/40 text-destructive hover:bg-destructive hover:text-white transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
  );
};
